import classNames from "classnames";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import { useFormikContext, Form } from "formik";
import { FC, useMemo } from "react";
import { FilterMultiselect } from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { Button } from "@design-system/Button/Button";
import { FirmSize } from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import fieldSpacing from "../components/fieldSpacing.module.scss";
import { firmSizeLabels } from "@components/entity/constants";
import animation from "../layout/OnboardingLayout.module.scss";
import {
    CreateOrganizationFormSchema,
    servicesOfferedOptions,
} from "../organizationOnboardingForms";
import commonStyles from "./common.module.scss";

const fields = createFormikFields<CreateOrganizationFormSchema>();

const firmSizeItems = Object.values(FirmSize).map((value) => ({
    value,
    label: firmSizeLabels[value],
}));

const isOrganizationService = (
    value: string | number,
): value is OrganizationServicesOffered =>
    typeof value === "string" &&
    Object.values(OrganizationServicesOffered).some(
        (service) => service === value,
    );

interface Props {
    validator: (values: CreateOrganizationFormSchema) => boolean;
    next: () => void;
}

export const CreateOrganization: FC<Props> = ({ validator, next }) => {
    const { values } = useFormikContext<CreateOrganizationFormSchema>();

    const isValid = useMemo(() => validator(values), [values, validator]);

    return (
        <div className={commonStyles.content420}>
            <header className={animation.fadeIn2}>
                <h1
                    className={classNames(
                        commonStyles.title,
                        "mb-4",
                        "text-center",
                    )}
                >
                    Create your firm
                </h1>
            </header>

            <Form>
                <fields.Text
                    name="organizationName"
                    label="Firm Name"
                    size="xl"
                    className={fieldSpacing.inputField}
                />

                <FormikField<FirmSize>
                    name="firmSize"
                    label="Number of clients served"
                    control="button"
                    className={fieldSpacing.buttonField}
                >
                    {({ helpers, field }) => (
                        <Picker.Select
                            items={firmSizeItems}
                            value={field.value ?? null}
                            onValueChange={(value) => {
                                if (value !== null) {
                                    helpers.setValue(value);
                                }
                            }}
                            placeholder="Select"
                        />
                    )}
                </FormikField>

                <FormikField<OrganizationServicesOffered[]>
                    name="servicesOffered"
                    label="Practices"
                    control="button"
                    className={fieldSpacing.buttonField}
                >
                    {({ helpers, field }) => (
                        <FilterMultiselect
                            name={field.name}
                            onBlur={() => helpers.setTouched(true)}
                            value={field.value}
                            onChange={(value) =>
                                helpers.setValue(
                                    value.filter(isOrganizationService),
                                )
                            }
                            options={servicesOfferedOptions}
                            searchable={false}
                        />
                    )}
                </FormikField>

                <footer className="mt-4">
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        block
                        disabled={!isValid}
                        onClick={next}
                        rightIcon={<EnterKeyHint />}
                    >
                        Create firm
                    </Button>
                </footer>
            </Form>
        </div>
    );
};
